import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	QueryList,
	SimpleChanges,
	ViewChildren,
} from "@angular/core";
import { UntypedFormArray, UntypedFormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { CONSTANT } from "src/app/constant";
import { CandidateRole } from "src/app/models/enum";
import { AssessmentConfigRes, ManeuverOutcomeDraft, OBComments } from "src/app/models/models";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";
import { SharedService } from "src/app/shared/services/shared.service";
import { ContainerRatingsService } from "../../../../services/base/container-ratings.services";
import {
	captureAllManeuverRepeatBaselines,
	crewRoleToObSlot,
	ObRoleSlot,
} from "../../../../shared-v2/utils/observation.utils";
import {
	filterTs1Step2SummaryComments,
	isTs1InstructorCommentVisible,
} from "../../../preview/ts1-preview-summary-comments.utils";
import {
	getSafeCandidateRating,
	syncManeuverOutcomeNaCascade,
	syncTs1V3RatingSummaryFromManeuverForms,
} from "../../../utils/ts1-rating-ob-interaction";
import {
	appendOutcomeToSelectedOBs,
	applyOutcomeSummaryCommentToDraft,
	ensureManeuverOutcomeHistoryInSelectedOBs,
	maneuverOutcomeHasActivity,
	markManeuverOutcomeSelectedOBsRepeatedForCandidate,
	OutcomeSlot,
	resetManeuverOutcomesForCandidateRepeat,
	TEM_SAFETY_OUTCOME_LABEL,
} from "../../../utils/ts1-rating-outcome-interaction";
import { isCompetencyManeuverCode } from "../../../utils/ts1-rating-review.utils";
import { isTs1SummaryObCommentMissing } from "../../../utils/ts1-rating-step3-validation.utils";
import { formatTs1SummaryManeuverLabel } from "../../../utils/ts1-summary-display.utils";
import { Ts1RatingObPanelComponent } from "../../../widgets/ts1-rating-ob-panel/ts1-rating-ob-panel.component";

interface ManeuverSidebarEntry {
	observationIndex: number;
	label: string;
	code: string;
}

@Component({
	selector: "app-ts1-rating-step2-v2",
	templateUrl: "./ts1-rating-step2-v2.component.html",
	styleUrls: ["./ts1-rating-step2-v2.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class Ts1RatingStep2V2Component implements OnInit, OnChanges {
	@ViewChildren("obPanelRef") obPanelRefs?: QueryList<Ts1RatingObPanelComponent>;

	private get obPanel(): Ts1RatingObPanelComponent | undefined {
		return this.obPanelRefs?.first;
	}

	@Input() ratingForm!: UntypedFormArray;
	@Input() loaded = false;
	@Input() candidateNames: string[] = [];
	/** Role per fixed candidate slot (0 = left header, 1 = right header). */
	@Input() candidateCrewRoles: CandidateRole[] = [CandidateRole.PF, CandidateRole.PM];
	@Input() maneuverOutcomes: ManeuverOutcomeDraft[] = [];
	@Input() assessmentConfig: AssessmentConfigRes[] = [];
	@Input() obCommentsEnabled = true;
	@Input() showConditionalObHighlight = false;

	@Output() swapRoles = new EventEmitter<void>();
	@Output() maneuverOutcomesChange = new EventEmitter<ManeuverOutcomeDraft[]>();
	/** Parent caches Repeat disabled — re-sync when the active sidebar maneuver changes. */
	@Output() activeManeuverChange = new EventEmitter<void>();

	readonly CANDIDATE_ROLE = CONSTANT.CANDIDATE_ROLE;
	readonly temSafetyOutcomeLabel = TEM_SAFETY_OUTCOME_LABEL;
	readonly CandidateRole = CandidateRole;
	readonly hasInstructorComment = isTs1InstructorCommentVisible;
	readonly isMissingRequiredComment = isTs1SummaryObCommentMissing;

	activeManeuverIndex = 0;
	obComments: OBComments[] = [];
	private readonly emptyOutcomeByCode = new Map<string, ManeuverOutcomeDraft>();

	get displayObComments(): OBComments[] {
		const cr = getSafeCandidateRating(this.containerRatingsService.draft);
		return filterTs1Step2SummaryComments(this.obComments, cr.addRemoveOb);
	}

	summaryManeuverLabel(comment: OBComments): string {
		return formatTs1SummaryManeuverLabel(comment, this.resetManeuvers);
	}

	private get resetManeuvers() {
		return getSafeCandidateRating(this.containerRatingsService.draft)?.resetManeuvers ?? [];
	}
	obPanelKey = "pf-pm-0";
	/** Stable single-item list for @for — only replaced when {@link refreshObPanelKey} runs. */
	obPanelKeys: string[] = [this.obPanelKey];

	constructor(
		public sharedService: SharedService,
		private dialog: MatDialog,
		private containerRatingsService: ContainerRatingsService,
		private cd: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.refreshObPanelKey();

		this.containerRatingsService.getClearComments().subscribe((val) => {
			if (val) {
				this.obComments = [];
			}
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["loaded"]?.currentValue || (this.loaded && changes["ratingForm"])) {
			if (this.loaded && this.ratingForm) {
				captureAllManeuverRepeatBaselines(this.ratingForm);
			}
			this.normalizeActiveManeuverIndex();
			this.syncSummaryFromManeuverForms();
			if (this.loaded) {
				this.scheduleObPanelStarRefresh();
			}
		}
		if (changes["candidateCrewRoles"]) {
			this.refreshObPanelKey();
			this.cd.markForCheck();
		}
		if (changes["assessmentConfig"] && this.loaded) {
			this.scheduleObPanelStarRefresh();
		}
		if (changes["candidateNames"] || changes["ratingForm"]) {
			this.cd.markForCheck();
		}
	}

	get canSwapRoles(): boolean {
		return this.hasSecondCandidate;
	}

	get hasSecondCandidate(): boolean {
		const nameCount = this.candidateNames?.length ?? 0;
		const formCount = this.ratingForm?.length ?? 0;
		return nameCount >= 2 || formCount >= 2;
	}

	get leftCandidateName(): string {
		return this.candidateNames[0] ?? "";
	}

	get rightCandidateName(): string {
		if (!this.hasSecondCandidate) {
			return this.candidateNames[0] ?? "";
		}
		return this.candidateNames[1] ?? "";
	}

	get leftRoleLabel(): string {
		return this.roleLabel(this.candidateCrewRoles[0]);
	}

	get rightRoleLabel(): string {
		return this.roleLabel(this.candidateCrewRoles[1]);
	}

	get leftCandidateRole(): CandidateRole {
		return this.candidateCrewRoles[0] ?? CandidateRole.PF;
	}

	get rightCandidateRole(): CandidateRole {
		return this.candidateCrewRoles[1] ?? CandidateRole.PM;
	}

	get leftObSlot(): ObRoleSlot {
		return crewRoleToObSlot(this.leftCandidateRole);
	}

	get rightObSlot(): ObRoleSlot {
		return crewRoleToObSlot(this.rightCandidateRole);
	}

	get leftManeuverForm(): UntypedFormGroup | null {
		return this.getManeuverForm(0);
	}

	get rightManeuverForm(): UntypedFormGroup | null {
		return this.getManeuverForm(this.hasSecondCandidate ? 1 : 0);
	}

	get pfCandidateIndex(): number {
		return this.candidateCrewRoles.findIndex((r) => r === CandidateRole.PF);
	}

	get pmCandidateIndex(): number {
		return this.candidateCrewRoles.findIndex((r) => r === CandidateRole.PM);
	}

	get pmCandidateName(): string {
		const idx = this.pmCandidateIndex;
		return idx >= 0 ? (this.candidateNames[idx] ?? "") : "";
	}

	get pfCandidateName(): string {
		const idx = this.pfCandidateIndex;
		return idx >= 0 ? (this.candidateNames[idx] ?? "") : "";
	}

	get maneuvers(): string[] {
		return this.getManeuverSidebarEntries().map((entry) => entry.label);
	}

	get maneuverCodes(): string[] {
		return this.getManeuverSidebarEntries().map((entry) => entry.code);
	}

	get activeObservationIndex(): number {
		return this.observationIndexForSidebar(this.activeManeuverIndex);
	}

	get activeManeuverCode(): string {
		return this.maneuverCodes[this.activeManeuverIndex] ?? "";
	}

	get activeManeuverLabel(): string {
		return this.maneuvers[this.activeManeuverIndex] ?? "";
	}

	get pfCandidateId(): string {
		return this.candidateIdForRoleIndex(this.pfCandidateIndex);
	}

	get pmCandidateId(): string {
		return this.candidateIdForRoleIndex(this.pmCandidateIndex);
	}

	get leftCandidateId(): string {
		return this.candidateIdForRoleIndex(0);
	}

	get rightCandidateId(): string {
		return this.candidateIdForRoleIndex(this.hasSecondCandidate ? 1 : 0);
	}

	get activeOutcome(): ManeuverOutcomeDraft | null {
		const code = this.activeManeuverCode;
		if (!code) return null;
		const found = this.maneuverOutcomes.find((o) => o.maneuverCode === code);
		if (found) return found;
		let empty = this.emptyOutcomeByCode.get(code);
		if (!empty) {
			empty = { maneuverCode: code };
			this.emptyOutcomeByCode.set(code, empty);
		}
		return empty;
	}

	get repeatDisabled(): boolean {
		// Outcome activity does not need the OB panel ViewChild (avoids stale disabled after draft restore).
		if (maneuverOutcomeHasActivity(this.activeOutcome)) return false;
		return this.obPanel?.repeatDisabled ?? true;
	}

	onRepeatClick(): void {
		this.obPanel?.repeatConfirmModal();
	}

	private roleLabel(role: CandidateRole | undefined): string {
		if (role === CandidateRole.PF) return "PF";
		if (role === CandidateRole.PM) return "PM";
		return "";
	}

	getCommentCandidateLabel(comment: OBComments): string {
		return this.candidateSlotLabel(this.candidateIndexForId(comment.candidateId));
	}

	getCommentCandidateLabelClass(comment: OBComments): string {
		const idx = this.candidateIndexForId(comment.candidateId);
		if (idx === 0) return "ts1-summary-candidate-label--1";
		if (idx === 1) return "ts1-summary-candidate-label--2";
		return "";
	}

	private candidateIndexForId(candidateId: string | undefined): number {
		if (!candidateId || !this.ratingForm?.length) return -1;
		for (let i = 0; i < this.ratingForm.length; i++) {
			const obs = this.ratingForm.at(i).get("observations") as UntypedFormArray | null;
			const id = (obs?.at(0) as UntypedFormGroup | undefined)?.get("candidateId")?.value;
			if (id === candidateId) return i;
		}
		return -1;
	}

	private candidateSlotLabel(index: number): string {
		if (index < 0) return "";
		if (index === 0) return "Candidate 1";
		if (index === 1) return "Candidate 2";
		return `Candidate ${index + 1}`;
	}

	private getObservations(candidateIndex: number): UntypedFormArray | null {
		return this.ratingForm?.at(candidateIndex)?.get("observations") as UntypedFormArray | null;
	}

	private getManeuverForm(candidateIndex: number): UntypedFormGroup | null {
		const obs = this.getObservations(candidateIndex);
		if (!obs || this.activeManeuverIndex < 0) return null;
		const observationIndex = this.observationIndexForSidebar(this.activeManeuverIndex);
		return obs.at(observationIndex) as UntypedFormGroup;
	}

	private getManeuverSidebarEntries(): ManeuverSidebarEntry[] {
		const first = this.ratingForm?.at(0)?.get("observations") as UntypedFormArray | null;
		if (!first) return [];

		return first.controls
			.map((control, observationIndex) => {
				const group = control as UntypedFormGroup;
				return {
					observationIndex,
					label: String(group.get("maneuver")?.value ?? ""),
					code: String(group.get("maneuverCode")?.value ?? ""),
				};
			})
			.filter((entry) => entry.code && !isCompetencyManeuverCode(entry.code));
	}

	private observationIndexForSidebar(sidebarIndex: number): number {
		return this.getManeuverSidebarEntries()[sidebarIndex]?.observationIndex ?? sidebarIndex;
	}

	private normalizeActiveManeuverIndex(): void {
		const count = this.maneuvers.length;
		if (!count) {
			this.activeManeuverIndex = 0;
			return;
		}
		if (this.activeManeuverIndex >= count) {
			this.activeManeuverIndex = count - 1;
		}
	}

	private candidateIdForRoleIndex(roleIndex: number): string {
		if (roleIndex < 0) return "";
		const formIndex = this.hasSecondCandidate ? roleIndex : 0;
		return this.getManeuverForm(formIndex)?.get("candidateId")?.value ?? "";
	}

	onManeuverSelect(index: number): void {
		this.activeManeuverIndex = index;
		this.refreshObPanelKey();
		this.scheduleObPanelStarRefresh();
		this.activeManeuverChange.emit();
	}

	onSwapRoles(): void {
		if (!this.canSwapRoles) return;

		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "logout-modal",
			height: "200px",
			width: "420px",
			hasBackdrop: true,
			data: {
				modalTitle: "Confirmation",
				modalContent: "Are you sure you want to switch candidate roles ?",
				modalBtnConfirmText: "Continue",
				modalBtnCancelText: "Cancel",
			},
		});

		dialogRef.afterClosed().subscribe((result: { close?: boolean } | undefined) => {
			if (result?.close) {
				this.swapRoles.emit();
			}
		});
	}

	private refreshObPanelKey(): void {
		const next = `${this.leftObSlot}-${this.rightObSlot}-${this.activeManeuverIndex}`;
		if (next === this.obPanelKey) return;
		this.obPanelKey = next;
		this.obPanelKeys = [next];
	}

	/** Rebuild inline Summary from maneuver form selectedOBs (after draft reload or Step 1 re-entry). */
	resyncSummary(): void {
		this.syncSummaryFromManeuverForms();
	}

	/** Refresh predefined OB star icons after async form/draft hydration. */
	refreshObPanelStars(): void {
		this.scheduleObPanelStarRefresh();
	}

	expandAllObSections(): void {
		this.obPanel?.expandAllSections();
	}

	collapseAllObSections(): void {
		this.obPanel?.collapseAllSections();
	}

	private scheduleObPanelStarRefresh(): void {
		const attempt = (tries = 0): void => {
			const panel = this.obPanelRefs?.first ?? this.obPanel;
			if (panel) {
				panel.refreshPredefinedStars();
				this.cd.markForCheck();
				return;
			}
			if (tries < 20) {
				setTimeout(() => attempt(tries + 1), tries < 4 ? 0 : 16);
			}
		};
		setTimeout(() => attempt(), 0);
	}

	private syncSummaryFromManeuverForms(): void {
		if (!this.loaded || !this.ratingForm) return;
		const cr = getSafeCandidateRating(this.containerRatingsService.draft);
		const outcomesUsed = cr.maneuverOutcomes?.length ? cr.maneuverOutcomes : this.maneuverOutcomes;
		syncTs1V3RatingSummaryFromManeuverForms(
			this.obComments,
			this.ratingForm,
			cr.selectedOBs,
			this.candidateCrewRoles,
			cr.addRemoveOb,
			cr.resetManeuvers,
			outcomesUsed,
		);
		this.cd.markForCheck();
	}

	onObComment(_comment: unknown): void {
		this.syncSummaryFromManeuverForms();
	}

	/** After maneuver repeat — reset OBs, clear Maneuver Outcome, resync summary. */
	onManeuverRepeated(event: { maneuverCode: string; candidateIds: string[] }): void {
		let clearedOutcomes = this.maneuverOutcomes;
		const cr = getSafeCandidateRating(this.containerRatingsService.draft);
		let selectedOBs = cr.selectedOBs ?? [];

		for (const candidateId of event.candidateIds) {
			const candidateIndex = this.candidateIndexForId(candidateId);
			clearedOutcomes = resetManeuverOutcomesForCandidateRepeat(
				clearedOutcomes,
				event.maneuverCode,
				candidateIndex >= 0 ? candidateIndex : 0,
				this.hasSecondCandidate,
			);
			selectedOBs = markManeuverOutcomeSelectedOBsRepeatedForCandidate(selectedOBs, event.maneuverCode, candidateId);
		}

		this.maneuverOutcomes = clearedOutcomes;
		this.maneuverOutcomesChange.emit(clearedOutcomes);
		this.containerRatingsService.updateSelectedDraft({
			candidateRating: { ...cr, maneuverOutcomes: clearedOutcomes, selectedOBs },
			lastUpdatedDate: new Date().toISOString(),
			continuePage: 2,
		});

		this.resyncSummary();
		this.cd.detectChanges();
	}

	onOutcomeChange(outcome: ManeuverOutcomeDraft): void {
		const previous = this.maneuverOutcomes.find((o) => o.maneuverCode === outcome.maneuverCode);
		const nextOutcome = syncManeuverOutcomeNaCascade({
			previous,
			outcome,
			containerRatingsService: this.containerRatingsService,
			formForSlot: (slot) => this.formForOutcomeSlot(slot),
		});

		const next = [...this.maneuverOutcomes];
		const idx = next.findIndex((o) => o.maneuverCode === nextOutcome.maneuverCode);
		if (idx >= 0) next[idx] = nextOutcome;
		else next.push(nextOutcome);

		const cr = getSafeCandidateRating(this.containerRatingsService.draft);
		// Keep selectedOBs aligned with outcome draft even if outcomeComment is skipped
		// (e.g. missing candidateId inputs) — otherwise N/A seats never become evaluations.
		const leftId =
			this.leftCandidateId || (this.leftManeuverForm?.get("candidateId")?.value as string | undefined) || "";
		const rightId =
			this.rightCandidateId || (this.rightManeuverForm?.get("candidateId")?.value as string | undefined) || leftId;
		const selectedOBs =
			nextOutcome.maneuverCode && (leftId || rightId)
				? ensureManeuverOutcomeHistoryInSelectedOBs(
						cr.selectedOBs ?? [],
						next,
						nextOutcome.maneuverCode,
						this.activeManeuverLabel || nextOutcome.maneuverCode,
						{ pf: leftId || rightId, pm: rightId || leftId },
						this.hasSecondCandidate,
					)
				: (cr.selectedOBs ?? []);

		this.containerRatingsService.updateSelectedDraft({
			candidateRating: { ...cr, maneuverOutcomes: next, selectedOBs },
			lastUpdatedDate: new Date().toISOString(),
			continuePage: 2,
		});

		this.maneuverOutcomesChange.emit(next);
		this.syncSummaryFromManeuverForms();
		this.obPanel?.refreshView();
		this.cd.detectChanges();
	}

	/** Candidate form for a Maneuver Outcome storage slot (left = index 0, right = index 1). */
	private formForOutcomeSlot(slot: OutcomeSlot): UntypedFormGroup | null {
		if (slot === "pfPrimary" || slot === "pm") {
			return this.leftManeuverForm;
		}
		return this.rightManeuverForm;
	}

	onOutcomeComment(comment: OBComments): void {
		const cr = getSafeCandidateRating(this.containerRatingsService.draft);
		// Draft already has cascaded snapshots from onOutcomeChange (same tick as outcomeComment).
		const maneuverOutcomes = applyOutcomeSummaryCommentToDraft(
			cr.maneuverOutcomes?.length ? cr.maneuverOutcomes : this.maneuverOutcomes,
			comment,
		);
		const selectedOBs = appendOutcomeToSelectedOBs(cr.selectedOBs ?? [], comment);

		this.maneuverOutcomesChange.emit(maneuverOutcomes);
		this.containerRatingsService.updateSelectedDraft({
			candidateRating: { ...cr, maneuverOutcomes, selectedOBs },
			lastUpdatedDate: new Date().toISOString(),
			continuePage: 2,
		});
		this.syncSummaryFromManeuverForms();
		this.cd.markForCheck();
	}

	getOutcomeRateClass(rate: number | undefined): string {
		if (rate === 1) return "text-red";
		if (rate === 2) return "text-yellow";
		if (rate === 3) return "text-yellow-60";
		if (rate === 4) return "text-green";
		if (rate === 5) return "text-green-90";
		return "text-grey-60";
	}

	viewSummaryModal(): void {
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "view-rating-summary-modal",
			height: "560px",
			maxHeight: "90vh",
			width: "1154px",
			maxWidth: "95vw",
			hasBackdrop: true,
			data: {
				modalTitle: "Comments",
				modalName: "viewTs1V3SummaryModal",
				modalContent: this.displayObComments,
				modalTraineeAssessmentId: this.leftManeuverForm?.get("traineeAssessmentId")?.value,
				noCloseBtn: true,
				modalBtnConfirmText: "Close",
			},
		});

		dialogRef.afterClosed().subscribe(() => {
			this.resyncSummary();
		});
	}

	get mergedObComments(): OBComments[] {
		const draft = getSafeCandidateRating(this.containerRatingsService.draft);
		return draft.selectedOBs ?? [];
	}
}
